export type ContentTypeKey =
  | "VideoLesson"
  | "VideoWithCodingExercise"
  | "HandsOnLesson"
  | "CodingExerciseItem"
  | "CodingChallenge"
  | "CtfChallenge"
  | "HandsOnLessonWithTests"
  | "HandsOnLessonWithoutTests"
  | "AppLab";

export const CONTENT_TYPE_OPTIONS: { value: ContentTypeKey; label: string; description: string }[] =
  [
    {
      value: "HandsOnLesson",
      label: "Break/Fix Lesson",
      description:
        "Learn how vulnerabilities work and how to remediate them in a live sandbox environment by first exploiting a vulnerability as an attacker and then switching to a defender role and patching the insecure code.\n\nThese labs are available in multiple languages and frameworks via a simple in-browser editor. After making changes, test your fix to confirm the issue is resolved.",
    },
    {
      value: "CodingChallenge",
      label: "Coding Challenge",
      description:
        "Practice secure coding by exploring the source code of a vulnerable application with less guidance and more open-ended problem solving. You'll practice code review, implement a fix, and validate your solution through testing in these robust challenges.",
    },
    {
      value: "CodingExerciseItem",
      label: "Coding Exercise",
      description:
        "Remediate insecure code in realistic scenarios in these short, language-specific labs. You'll find these appended to many video lessons, and they're a great way to reinforce what you just learned.",
    },
    {
      value: "CtfChallenge",
      label: "CTF Challenge",
      description:
        "You'll be in the role of an attacker, attempting to compromise a vulnerable website by applying the skills you've learned, and exfiltrating a flag to complete your challenge.\n\nDifficulty varies, and instructions are intentionally vague.",
    },
    {
      value: "VideoLesson",
      label: "Video Lesson",
      description:
        "Digestible, podcast-style video lessons that teach secure coding best practices. You can watch the video, read the lesson text, or do both side-by-side, so you can learn in the format that works best for you.\n\nThe lesson concludes with a five-question quiz to reinforce learning.",
    },
    {
      value: "VideoWithCodingExercise",
      label: "Video Lesson with Coding Exercises",
      description:
        "Digestible, podcast-style video lessons that teach secure coding best practices. You can watch the video, read the lesson text, or do both side-by-side, so you can learn in the format that works best for you.\n\nIn addition to a five-question quiz, these lessons also include an optional Coding Exercise that complements what you've just learned.",
    },
    {
      value: "AppLab",
      label: "AI Skills Lab",
      description:
        "Learn how to leverage AI with confidence in your everyday work in a live hands-on environment, with no coding required.",
    },
  ];

export const CONTENT_TYPE_OPTIONS_WITH_TESTS: {
  value: ContentTypeKey;
  label: string;
  description: string;
}[] = (
  [
    ...CONTENT_TYPE_OPTIONS,
    {
      value: "HandsOnLessonWithoutTests",
      label: "Break/Fix Lesson (no test)",
      description:
        "Learn how vulnerabilities work and how to remediate them in a live sandbox environment by first exploiting a vulnerability as an attacker and then switching to a defender role and patching the insecure code.\n\nThese labs are available in multiple languages and frameworks via a simple in-browser editor, but do not include a test.",
    },
  ] as {
    value: ContentTypeKey;
    label: string;
    description: string;
  }[]
).sort((a, b) => a.label.localeCompare(b.label));

// SHRPA-2822 -- The following should come from the Factory API but are hardcoded here for now
export const difficultyDisplayNames: { [key: string]: string } = {
  awareness: "Awareness",
  basic: "Basic",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

// difficulty order
export const difficultyOrder = ["basic", "intermediate", "advanced", "awareness"];

export const focusDisplayNames: { [key: string]: string } = {
  "business learner": "Business Learner",
  compliance: "Compliance",
  "data scientist": "Data Scientist",
  developer: "Developer",
  devsecops: "DevSecOps",
  everyone: "Everyone",
  executive: "Executive",
  "mobile developer": "Mobile Developer",
  "native developer": "Native Developer",
  topic: "Topic",
  "web developer - backend": "Web Developer - Backend",
  "web developer - frontend": "Web Developer - Frontend",
};

export const RELEASE_OPTIONS: { value: string; label: string }[] = [
  { value: "recently-released", label: "Recently Released" },
];

export const LESSONS_RELEASE_OPTIONS: { value: string; label: string }[] = [
  { value: "recently", label: "Recently Released" },
];

export const languageDisplayNames: { [key: string]: string } = {
  apex: "Apex",
  c: "C",
  clojure: "Clojure",
  cobol: "COBOL",
  cpp: "C++",
  csharp: "C#",
  dotnet: ".NET",
  elixir: "Elixir",
  go: "Go",
  golang: "Go",
  java: "Java",
  javascript: "JavaScript",
  kotlin: "Kotlin",
  node: "Node.js",
  perl: "Perl",
  php: "PHP",
  pseudocode: "Pseudocode",
  python: "Python",
  r: "R",
  ruby: "Ruby",
  rust: "Rust",
  scala: "Scala",
  shell: "Shell",
  swift: "Swift",
  typescript: "TypeScript",
};

export const technologyDisplayNames: { [key: string]: string } = {
  "ai/llm": "AI/LLM",
  api: "API",
  aws: "AWS",
  azure: "Azure",
  blockchain: "Blockchain",
  docker: "Docker",
  embedded: "Embedded",
  gcp: "GCP",
  graphql: "GraphQL",
  grpc: "gRPC",
  iac: "Infrastructure as Code (IaC)",
  kubernetes: "Kubernetes",
  privacy: "Privacy",
  securitytesting: "Security Testing",
  "supply chain": "Supply Chain",
  terraform: "Terraform",
};

export const getLanguageDisplayName = (value: string): string => {
  if (!value) return "";
  if (value === "c++") return "C++";
  if (value === "c#") return "C#";
  return (
    languageDisplayNames[value] || value.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  );
};

export const getTechnologyDisplayName = (value: string): string => {
  if (!value) return "";
  return (
    technologyDisplayNames[value] || value.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  );
};

export const LESSONS_CONTENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "app_lab", label: "AI Skills Lab" },
  { value: "hackedu", label: "Break/Fix" },
  { value: "code_challenge", label: "Coding Challenge" },
  { value: "hacking_challenge", label: "CTF Challenge" },
  { value: "only_video", label: "Video Only" },
  { value: "with_experiments", label: "With Coding Exercises" },
];

export const COLLECTION_CONTENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "handson", label: "Hands-On" },
  { value: "video", label: "Video" },
  { value: "mixed", label: "Mixed" },
];

export const CUSTOM_COLLECTIONS_OPTIONS: { value: string; label: string }[] = [
  { value: "custom", label: "Custom" },
  { value: "recommended", label: "Recommended" },
];
